import React, { useMemo } from 'react';
import { Sun, Thermometer, Droplet, Droplets, CloudRain, Gauge, Wind } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '../../hooks/useAnalytics';
import { InfoTooltipButton } from './InfoTooltipButton';
import { CustomGHITooltip } from './CustomGHITooltip';
import { MetricComparisonCard } from './MetricComparisonCard';
import { WaterSummary } from './WaterSummary';
import './OverviewTab.css';

export function OverviewTab() {
  const { hourlyData, dailyData, timeRange } = useAnalytics();
  const { t } = useTranslation();

  const avgBy = (arr, key) => {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    const sum = arr.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);
    return sum / arr.length;
  };

  const sumBy = (arr, key) => {
    if (!Array.isArray(arr) || arr.length === 0) return 0;
    return arr.reduce((acc, row) => acc + (Number(row?.[key]) || 0), 0);
  };

  // Prepare GHI chart data (last 24 hours, 96 data points at 15-min intervals)
  const ghiChartData = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0 || retro.length === 0) return [];
    
    // Get last 96 points (24 hours * 4 points per hour)
    const last96Campo = campo.slice(-96);
    const last96Retro = retro.slice(-96);
    
    return last96Campo.map((c, i) => {
      const r = last96Retro[i] || {};
      const campoGhi = c.ghi_wm2 || 0;
      const retroGhi = r.ghi_wm2 || 0;
      
      return {
        time: c.ts_iso,
        campo_ghi: campoGhi,
        retro_ghi: retroGhi,
        gap: campoGhi - retroGhi,
      };
    });
  }, [hourlyData]);

  // Calculate total ET for water summary
  const totalEtCampo = useMemo(() => {
    const campoDays = dailyData.FIELD || [];
    return campoDays.reduce((sum, day) => sum + (day.etc_mm || 0), 0);
  }, [dailyData]);

  const totalEtRetro = useMemo(() => {
    const retroDays = dailyData.UNDER || [];
    return retroDays.reduce((sum, day) => sum + (day.etc_mm || 0), 0);
  }, [dailyData]);

  // Get period label
  const periodLabel = useMemo(() => {
    const fieldDays = dailyData.FIELD || [];
    if (fieldDays.length === 0) return t('analytics.overview.selectedPeriod');
    const startDate = fieldDays[0]?.date;
    const endDate = fieldDays[fieldDays.length - 1]?.date;
    if (startDate && endDate) {
      return `${format(parseISO(startDate), 'MMM d')} - ${format(parseISO(endDate), 'MMM d, yyyy')}`;
    }
    return t('analytics.overview.selectedPeriod');
  }, [dailyData, t]);

  const comparisonSubtitle = useMemo(() => {
    const rangeText = timeRange?.id
      ? t(`analytics.timeRanges.${timeRange.id}`, { defaultValue: timeRange.label })
      : (timeRange?.label || '');
    return rangeText ? `${t('analytics.overview.selectedPeriod')} • ${rangeText}` : t('analytics.overview.selectedPeriod');
  }, [t, timeRange?.id, timeRange?.label]);

  // Average VPD values
  const avgVpdCampo = useMemo(() => {
    return avgBy(hourlyData.FIELD || [], 'vpd_kpa');
  }, [hourlyData]);

  const avgVpdRetro = useMemo(() => {
    return avgBy(hourlyData.UNDER || [], 'vpd_kpa');
  }, [hourlyData]);

  // Period aggregates (respect selected time range)
  const avgTempCampo = useMemo(() => avgBy(hourlyData.FIELD || [], 't_c'), [hourlyData]);
  const avgTempRetro = useMemo(() => avgBy(hourlyData.UNDER || [], 't_c'), [hourlyData]);

  const avgRhCampo = useMemo(() => avgBy(hourlyData.FIELD || [], 'rh_pct'), [hourlyData]);
  const avgRhRetro = useMemo(() => avgBy(hourlyData.UNDER || [], 'rh_pct'), [hourlyData]);

  const avgSoilCampo = useMemo(() => avgBy(hourlyData.FIELD || [], 'soil_vwc_pct_10cm'), [hourlyData]);
  const avgSoilRetro = useMemo(() => avgBy(hourlyData.UNDER || [], 'soil_vwc_pct_10cm'), [hourlyData]);

  const campoRainTotal = useMemo(() => {
    return sumBy(dailyData.FIELD || [], 'rain_mm');
  }, [dailyData]);

  const retroRainTotal = useMemo(() => {
    return sumBy(dailyData.UNDER || [], 'rain_mm');
  }, [dailyData]);

  // Calculate average PSH
  const campoPSH = useMemo(() => {
    const campoDays = dailyData.FIELD || [];
    if (campoDays.length === 0) return 0;
    const sum = campoDays.reduce((acc, day) => acc + (day.psh || 0), 0);
    return sum / campoDays.length;
  }, [dailyData]);

  const retroPSH = useMemo(() => {
    const retroDays = dailyData.UNDER || [];
    if (retroDays.length === 0) return 0;
    const sum = retroDays.reduce((acc, day) => acc + (day.psh || 0), 0);
    return sum / retroDays.length;
  }, [dailyData]);

  // Wind Direction Distribution (for radar chart)
  const windDirectionDistribution = useMemo(() => {
    const campo = hourlyData.FIELD || [];
    const retro = hourlyData.UNDER || [];
    
    if (campo.length === 0) return [];
    
    const directions = [
      t('analytics.wind.cardinal.n'),
      t('analytics.wind.cardinal.ne'),
      t('analytics.wind.cardinal.e'),
      t('analytics.wind.cardinal.se'),
      t('analytics.wind.cardinal.s'),
      t('analytics.wind.cardinal.sw'),
      t('analytics.wind.cardinal.w'),
      t('analytics.wind.cardinal.nw'),
    ];
    const directionAngles = [0, 45, 90, 135, 180, 225, 270, 315];
    
    // Initialize counters for each direction
    const campoCounts = Array(8).fill(0);
    const retroCounts = Array(8).fill(0);
    let campoTotal = 0;
    let retroTotal = 0;
    
    // Count wind occurrences by direction
    campo.forEach(d => {
      const speed = d.wind_ms || 0;
      const dir = d.wind_dir_deg || 0;
      
      // Only count if wind speed > 0.5 m/s (not calm)
      if (speed >= 0.5) {
        campoTotal++;
        const dirIndex = Math.round(dir / 45) % 8;
        campoCounts[dirIndex]++;
      }
    });
    
    retro.forEach(d => {
      const speed = d.wind_ms || 0;
      const dir = d.wind_dir_deg || 0;
      
      if (speed >= 0.5) {
        retroTotal++;
        const dirIndex = Math.round(dir / 45) % 8;
        retroCounts[dirIndex]++;
      }
    });
    
    // Convert to frequency percentages and create data for radar chart
    return directions.map((dir, idx) => ({
      direction: dir,
      field: campoTotal > 0 ? (campoCounts[idx] / campoTotal) * 100 : 0,
      under: retroTotal > 0 ? (retroCounts[idx] / retroTotal) * 100 : 0,
    }));
  }, [hourlyData, t]);

  return (
    <div className="overview-tab-container">
      {/* Featured GHI Chart */}
      <div className="overview-featured-chart">
        <div className="overview-featured-chart__header">
          <div className="overview-featured-chart__title-group">
            <Sun className="overview-featured-chart__icon" size={20} />
            <h2 className="overview-featured-chart__title">
              {t('analytics.overview.radiationOverviewTitle')}
            </h2>
          </div>
          <InfoTooltipButton content={t('analytics.overview.radiationOverviewTooltip')} />
        </div>
        
        <div className="overview-featured-chart__body overview-featured-chart__body--area">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={ghiChartData} margin={{ top: 20, right: 20, left: 20, bottom: 50 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EFEFEF" />
              
              <XAxis
                dataKey="time"
                stroke="#6B6B6B"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => format(parseISO(value), 'HH:mm')}
              />
              
              <YAxis
                stroke="#6B6B6B"
                tick={{ fontSize: 12 }}
                label={{ value: t('analytics.radiation.axes.ghiWm2'), angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
              />
              
              <Tooltip content={<CustomGHITooltip />} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
              
              <Legend
                verticalAlign="bottom"
                align="center"
                height={36}
                iconType="square"
                wrapperStyle={{ fontSize: '13px', bottom: 0 }}
              />
              
              {/* Campo GHI - Blue area (top) */}
              <Area
                type="monotone"
                dataKey="campo_ghi"
                name={t('stations.types.field')}
                stroke="#3A6A7A"
                strokeWidth={2}
                fill="#4A7A8A"
                fillOpacity={0.8}
              />
              
              {/* Retro GHI - Green area (bottom) */}
              <Area
                type="monotone"
                dataKey="retro_ghi"
                name={t('stations.types.underpanel')}
                stroke="#3A5A2A"
                strokeWidth={2}
                fill="#5A7A3A"
                fillOpacity={0.8}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Wind Direction Distribution */}
      <div className="overview-featured-chart">
        <div className="overview-featured-chart__header">
          <div className="overview-featured-chart__title-group">
            <Wind className="overview-featured-chart__icon" size={20} />
            <h2 className="overview-featured-chart__title">
              {t('analytics.overview.windDirectionDistributionTitle')}
            </h2>
          </div>
          <InfoTooltipButton content={t('analytics.overview.windDirectionDistributionTooltip')} />
        </div>
        
        <div className="overview-featured-chart__body overview-featured-chart__body--radar">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={windDirectionDistribution} margin={{ top: 20, right: 20, left: 20, bottom: 50 }}>
              <PolarGrid stroke="#E0E0E0" />
              <PolarAngleAxis 
                dataKey="direction" 
                tick={{ fontSize: 13, fill: '#6B6B6B' }}
                stroke="#6B6B6B"
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 'auto']}
                tick={{ fontSize: 11, fill: '#6B6B6B' }}
                stroke="#6B6B6B"
                tickFormatter={(value) => `${value}%`}
              />
              <Radar
                name={t('analytics.labels.field')}
                dataKey="field"
                stroke="#4A7A8A"
                fill="#4A7A8A"
                fillOpacity={0.6}
                strokeWidth={2}
              />
              <Radar
                name={t('analytics.labels.underPanel')}
                dataKey="under"
                stroke="#5A7A3A"
                fill="#5A7A3A"
                fillOpacity={0.6}
                strokeWidth={2}
              />
              <Tooltip
                isAnimationActive={false}
                wrapperStyle={{ transition: 'none' }}
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  padding: '8px 12px',
                }}
                formatter={(value) => [`${value.toFixed(1)}%`, '']}
                labelFormatter={(label) => `${t('analytics.wind.direction')}: ${label}`}
              />
              <Legend
                verticalAlign="bottom"
                align="center"
                height={36}
                iconType="square"
                wrapperStyle={{ fontSize: '13px', bottom: 0 }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="overview-metrics-grid">
        {/* Temperature */}
        <MetricComparisonCard
          icon={<Thermometer size={20} />}
          iconColor="#FF6B6B"
          iconBg="rgba(255, 107, 107, 0.08)"
          title={t('analytics.metrics.temperature')}
          campoValue={avgTempCampo}
          retroValue={avgTempRetro}
          unit="°C"
          tooltipContent={t('analytics.overview.tooltips.temperature')}
          comparisonSubtitle={comparisonSubtitle}
        />
        
        {/* Humidity */}
        <MetricComparisonCard
          icon={<Droplet size={20} />}
          iconColor="#4A7A8A"
          iconBg="rgba(74, 122, 138, 0.08)"
          title={t('analytics.overview.metrics.relativeHumidity')}
          campoValue={avgRhCampo}
          retroValue={avgRhRetro}
          unit="%"
          tooltipContent={t('analytics.overview.tooltips.humidity')}
          comparisonSubtitle={comparisonSubtitle}
        />
        
        {/* VPD (Vapor Pressure Deficit) */}
        <MetricComparisonCard
          icon={<Gauge size={20} />}
          iconColor="#8A4A3A"
          iconBg="rgba(138, 74, 58, 0.08)"
          title={t('analytics.metrics.vpd')}
          campoValue={avgVpdCampo}
          retroValue={avgVpdRetro}
          unit=" kPa"
          tooltipContent={t('analytics.overview.tooltips.vpd')}
          formatValue={(v) => v.toFixed(2)}
          comparisonSubtitle={comparisonSubtitle}
        />
        
        {/* Solar Radiation */}
        <MetricComparisonCard
          icon={<Sun size={20} />}
          iconColor="#FFA500"
          iconBg="rgba(255, 165, 0, 0.08)"
          title={t('analytics.overview.metrics.dailySolarRadiation')}
          campoValue={campoPSH}
          retroValue={retroPSH}
          unit=" PSH"
          tooltipContent={t('analytics.overview.tooltips.dailySolarRadiation')}
          formatValue={(v) => v.toFixed(1)}
          comparisonSubtitle={comparisonSubtitle}
        />
        
        {/* Soil Moisture */}
        <MetricComparisonCard
          icon={<Droplets size={20} />}
          iconColor="#8B4513"
          iconBg="rgba(139, 69, 19, 0.08)"
          title={t('analytics.overview.metrics.soilMoisture10cm')}
          campoValue={avgSoilCampo}
          retroValue={avgSoilRetro}
          unit="%"
          tooltipContent={t('analytics.overview.tooltips.soilMoisture10cm')}
          comparisonSubtitle={comparisonSubtitle}
        />
        
        {/* Rain */}
        <MetricComparisonCard
          icon={<CloudRain size={20} />}
          iconColor="#4682B4"
          iconBg="rgba(70, 130, 180, 0.08)"
          title={t('analytics.metrics.rainfall')}
          campoValue={campoRainTotal}
          retroValue={retroRainTotal}
          unit=" mm"
          tooltipContent={t('analytics.overview.tooltips.rainfall')}
          formatValue={(v) => v.toFixed(1)}
          comparisonSubtitle={comparisonSubtitle}
        />
      </div>

      {/* Water Balance Summary */}
      <WaterSummary
        rainfall={campoRainTotal}
        etCampo={totalEtCampo}
        etRetro={totalEtRetro}
        periodLabel={periodLabel}
      />
    </div>
  );
}
